// notificationService.js
// Comprehensive notification service for ClubHudl

import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  getDoc
} from 'firebase/firestore';
import { db, messaging } from '../services/firebase';
import { getToken, onMessage } from 'firebase/messaging';
import { formatDate } from '../utils/helpers';

/**
 * Request notification permission and get token
 * This MUST be called from a user interaction (button click)
 */
export const requestNotificationPermission = async (userId) => {
  try {
    console.log('🔔 Requesting notification permission...');

    // Check if messaging is supported
    if (!messaging) {
      console.log('⚠️ Firebase Messaging not supported in this browser');
      return { success: false, error: 'Messaging not supported' };
    }

    const permission = await Notification.requestPermission();

    if (permission === 'granted') {
      console.log('✅ Notification permission granted.');

      // Get the token
      // We need to use the service worker registration if available
      // But typically getToken handles it if firebase-messaging-sw.js exists
      const currentToken = await getToken(messaging, {
        vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY // Optional if using default
      });

      if (currentToken) {
        console.log('🔑 FCM Token:', currentToken);

        // Save token to user profile
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
          fcmToken: currentToken,
          notificationsEnabled: true,
          lastTokenUpdate: new Date().toISOString()
        });

        return { success: true, token: currentToken };
      } else {
        console.log('⚠️ No registration token available. Request permission to generate one.');
        return { success: false, error: 'No token available' };
      }
    } else {
      console.log('❌ Permission denied');
      return { success: false, error: 'Permission denied' };
    }
  } catch (err) {
    console.log('❌ An error occurred while retrieving token. ', err);
    return { success: false, error: err.message };
  }
};

/**
 * Listen for foreground messages
 */
export const onForegroundMessage = (callback) => {
  if (messaging) {
    return onMessage(messaging, (payload) => {
      console.log('🔔 Foreground message received:', payload);
      callback(payload);
    });
  }
  return null;
};




/**
 * Unified notification handler for Squad Publishing
 * Supports: 'silent', 'impacted_only', and 'all_roster' options
 */
export const processSquadPublishNotifications = async (options) => {
  const {
    game,
    newSquad,
    previousSquad,
    superSubs,
    newDuties,
    previousDuties,
    notificationOption,
    currentUserName,
    clubId
  } = options;

  console.log(`🏏 Processing squad notifications [Option: ${notificationOption}]`);

  if (notificationOption === 'silent' || !newSquad || newSquad.length === 0) {
    console.log('🔇 Silent mode or empty squad - skipping notifications');
    return { success: true, count: 0 };
  }

  const notificationsRef = collection(db, 'notifications');
  const notificationPromises = [];
  let successCount = 0;

  // Helper DUTY labels
  const DUTY_LABELS = {
    'ground_setup': '🏟️ Ground Setup',
    'refreshments': '🥤 Drinks & Snacks',
    'scoring': '📝 Scoring',
    'umpiring': '🧑‍⚖️ Umpiring',
    'first_aid': '🩹 First Aid',
    'equipment': '🎒 Equipment',
    'transport': '🚗 Transport',
    'photography': '📸 Photography'
  };

  const formattedDate = formatDate(game.date, {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });

  try {
    // Determine player groups based on option
    let playersToNotifySelection = []; // Players getting "Selected" or "Super Sub"
    let playersToNotifyRemoval = []; // Players getting "Removed"
    let playersToNotifyNotSelected = []; // Players getting "Not Selected"

    const newlyAdded = newSquad.filter(id => !previousSquad.includes(id) && !id.startsWith('guest'));
    const newlyRemoved = previousSquad.filter(id => !newSquad.includes(id) && !id.startsWith('guest'));
    const allRosterFiltered = []; // Will hold roster players who said Yes/Maybe/Unresponded

    if (notificationOption === 'impacted_only') {
      playersToNotifySelection = newlyAdded;
      playersToNotifyRemoval = newlyRemoved;
    } else if (notificationOption === 'all_roster') {
      // 1. Fetch team roster
      const teamId = game.teamId || game.homeTeamId;
      if (teamId) {
        let players = [];
        const teamRef = doc(db, 'teams', teamId);
        const teamSnap = await getDoc(teamRef);

        if (teamSnap.exists() && teamSnap.data().players) {
          players = teamSnap.data().players;
        } else {
          // Fallback
          const usersRef = collection(db, 'users');
          const q = query(usersRef, where('teamId', '==', teamId));
          const snapshot = await getDocs(q);
          snapshot.forEach(doc => players.push({ id: doc.id, ...doc.data() }));
        }

        // 2. Filter out anyone whose game availability is 'unavailable'
        players.forEach(p => {
          const id = p.userId || p.id || p.uid;
          if (!id) return;
          const availability = game.availability?.[id];
          if (availability?.status !== 'unavailable') {
            allRosterFiltered.push(id);
          }
        });

        playersToNotifySelection = newSquad.filter(id => !id.startsWith('guest'));
        playersToNotifyNotSelected = allRosterFiltered.filter(id => !newSquad.includes(id));
      } else {
        // Fallback if no team ID
        playersToNotifySelection = newSquad.filter(id => !id.startsWith('guest'));
      }
    }

    // A. Send Selection Notifications
    for (const playerId of playersToNotifySelection) {
      const isSuperSub = superSubs.includes(playerId);
      const assignedDuty = newDuties?.[playerId];
      const dutyLabel = assignedDuty ? DUTY_LABELS[assignedDuty] : null;

      let title = isSuperSub ? '⭐ Selected as Super Sub' : '✅ Selected for Squad';
      let message = `You've been selected ${isSuperSub ? 'as a super sub (50% cost)' : 'for the playing squad'} for ${game.name} on ${formattedDate}.`;

      const notificationData = {
        userId: playerId,
        type: 'squad_selection',
        read: false,
        createdAt: new Date().toISOString(),
        clubId: clubId,
        gameId: game.id,
        gameName: game.name,
        gameDate: formattedDate,
        gameVenue: game.venue || '',
        isSuperSub: isSuperSub,
        assignedDuty: dutyLabel,
        title: title,
        message: message,
        createdBy: currentUserName || 'Captain'
      };

      notificationPromises.push(addDoc(notificationsRef, notificationData).then(() => successCount++));
    }

    // B. Send Removal Notifications (Impacted Only Option)
    if (notificationOption === 'impacted_only') {
      for (const playerId of playersToNotifyRemoval) {
        const notificationData = {
          userId: playerId,
          type: 'squad_removal',
          read: false,
          createdAt: new Date().toISOString(),
          clubId: clubId,
          gameId: game.id,
          gameName: game.name,
          gameDate: formattedDate,
          title: '❌ Removed from Squad',
          message: `The squad was updated and you are no longer in the playing 11 for ${game.name}.`,
          createdBy: currentUserName || 'Captain'
        };
        notificationPromises.push(addDoc(notificationsRef, notificationData).then(() => successCount++));
      }
    }

    // C. Send Not Selected Notifications (All Roster Option)
    if (notificationOption === 'all_roster') {
      for (const playerId of playersToNotifyNotSelected) {
        const notificationData = {
          userId: playerId,
          type: 'squad_not_selected',
          read: false,
          createdAt: new Date().toISOString(),
          clubId: clubId,
          gameId: game.id,
          gameName: game.name,
          gameDate: formattedDate,
          title: 'Squad Published',
          message: `The squad for ${game.name} has been finalized. You are not in the playing squad for this match.`,
          createdBy: currentUserName || 'Captain'
        };
        notificationPromises.push(addDoc(notificationsRef, notificationData).then(() => successCount++));
      }
    }

    // D. New Duty Assignments (Always ping explicitly)
    const playersWithNewDuties = Object.keys(newDuties || {}).filter(playerId => {
      const previousDuty = previousDuties?.[playerId];
      const newDuty = newDuties[playerId];
      // Only ping if duty changed and we aren't ALREADY pinging them with a "Selected" notification right now
      return newDuty && newDuty !== previousDuty && !playerId.startsWith('guest') && !playersToNotifySelection.includes(playerId);
    });

    for (const playerId of playersWithNewDuties) {
      const dutyLabel = DUTY_LABELS[newDuties[playerId]];
      if (dutyLabel) {
        const notificationData = {
          userId: playerId,
          type: 'duty_assigned',
          read: false,
          createdAt: new Date().toISOString(),
          clubId: clubId,
          gameId: game.id,
          gameName: game.name,
          gameDate: formattedDate,
          title: '📋 New Duty Assigned',
          message: `You've been assigned: ${dutyLabel} for ${game.name}`,
          assignedDuty: dutyLabel,
          createdBy: currentUserName || 'Captain'
        };
        notificationPromises.push(addDoc(notificationsRef, notificationData).then(() => successCount++));
      }
    }

    await Promise.all(notificationPromises);

    console.log(`✅ Sent ${successCount} total squad publishing notifications`);
    return { success: true, count: successCount };

  } catch (error) {
    console.error('❌ Error creating squad notifications:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Notify when a player is added or removed from a team roster
 * Notifies the affected player, team captains, team vice-captains, and club admins
 */
export const notifyTeamRosterChange = async ({
  teamId,
  teamName,
  player,
  action,
  clubId,
  currentUserInfo
}) => {
  try {
    console.log(`🧍‍♂️ Processing team roster update: ${player.name} ${action} ${teamName}`);

    const currentUserUid = currentUserInfo?.uid || currentUserInfo?.id;
    const usersToNotify = new Set();
    const notificationsRef = collection(db, 'notifications');
    const notificationPromises = [];

    // 1. Notify the affected player (if they have an account and aren't the one doing it)
    const affectedUserId = player.userId || player.id || player.uid;
    if (affectedUserId && affectedUserId !== currentUserUid && !player.isGuest && !player.isExternal) {
      usersToNotify.add(affectedUserId);
    }

    // 2. Fetch team leadership (captains, VCs)
    let captains = [];
    let viceCaptains = [];
    try {
      const teamRef = doc(db, 'teams', teamId);
      const teamSnap = await getDoc(teamRef);
      if (teamSnap.exists()) {
        const teamData = teamSnap.data();
        captains = teamData.captains || [];
        viceCaptains = teamData.viceCaptains || [];

        captains.forEach(id => usersToNotify.add(id));
        viceCaptains.forEach(id => usersToNotify.add(id));
      }
    } catch (e) {
      console.warn('Could not fetch team leadership for notifications:', e);
    }

    // 3. Fetch Club Admins
    try {
      const usersRef = collection(db, 'users');
      // Legacy query
      const qLegacy = query(usersRef, where('clubInfo.clubId', '==', clubId), where('clubInfo.role', 'in', ['admin', 'super-admin']));
      // New multi-club query
      const qNew = query(usersRef, where('joinedClubsIds', 'array-contains', clubId));

      const [snap1, snap2] = await Promise.all([getDocs(qLegacy), getDocs(qNew)]);

      snap1.forEach(doc => usersToNotify.add(doc.id));
      snap2.forEach(doc => {
        const data = doc.data();
        const role = data.joinedClubs?.[clubId]?.role || data.clubInfo?.role;
        if (role === 'admin' || role === 'super-admin') {
          usersToNotify.add(doc.id);
        }
      });
    } catch (e) {
      console.warn('Could not fetch club admins for notifications:', e);
    }

    // Ensure we don't notify the person who actually performed the action
    if (currentUserUid) {
      usersToNotify.delete(currentUserUid);
    }

    if (usersToNotify.size === 0) {
      console.log('🔇 No users to notify for roster change.');
      return { success: true, count: 0 };
    }

    const actionText = action === 'added' ? 'added to' : 'removed from';
    const titleText = action === 'added' ? '✅ Roster Addition' : '❌ Roster Removal';

    for (const userId of usersToNotify) {
      // Customize message slightly if sending to the affected player themselves
      let message = `Player ${player.name} was ${actionText} the ${teamName} roster.`;

      if (userId === affectedUserId) {
        message = `You have been ${actionText} the ${teamName} roster.`;
      }

      const notificationData = {
        userId: userId,
        type: 'team_roster_update',
        read: false,
        createdAt: new Date().toISOString(),
        clubId: clubId,
        title: titleText,
        message: message,
        teamId: teamId,
        teamName: teamName,
        createdBy: currentUserInfo?.name || 'Club Admin'
      };

      notificationPromises.push(addDoc(notificationsRef, notificationData));
    }

    await Promise.all(notificationPromises);

    console.log(`✅ Sent ${notificationPromises.length} team roster update notifications`);
    return { success: true, count: notificationPromises.length };

  } catch (error) {
    console.error('❌ Error creating roster update notifications:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create notifications for team players when a new game is created
 * Only notifies players in the specific team roster
 */
export const notifyTeamPlayersAboutNewGame = async (game, teamId) => {
  try {
    console.log('📧 Creating game notifications for team:', teamId);

    // Get the team to find all players
    const teamRef = doc(db, 'teams', teamId);
    const teamSnap = await getDoc(teamRef);

    if (!teamSnap.exists()) {
      console.log('⚠️ Team not found in Firestore:', teamId);

      // Try alternative: get players directly from users collection
      console.log('🔄 Attempting to find players from users collection...');
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('teamId', '==', teamId));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const players = [];
        snapshot.forEach(doc => {
          players.push({ id: doc.id, ...doc.data() });
        });

        console.log(`✅ Found ${players.length} players in users collection for team ${teamId}`);

        // Continue with these players
        return await createGameNotificationsForPlayers(game, players, teamId, 'Team');
      }

      return { success: false, error: 'Team not found' };
    }

    const teamData = teamSnap.data();
    console.log('✅ Team found:', teamData.name);
    const players = teamData.players || teamData.roster || [];
    console.log(`📊 Team has ${players.length} players`);

    if (players.length === 0) {
      console.log('⚠️ No players in team roster');
      return { success: true, count: 0 };
    }

    return await createGameNotificationsForPlayers(game, players, teamId, teamData.name);
  } catch (error) {
    console.error('❌ Error creating game notifications:', error);
    return { success: false, error: error.message };
  }
};

// Helper function to create notifications for players
const createGameNotificationsForPlayers = async (game, players, teamId, teamName) => {
  const notificationsRef = collection(db, 'notifications');
  const notificationPromises = [];
  let successCount = 0;

  // Create notification for each team player
  for (const player of players) {
    const playerId = player.userId || player.id || player.uid;

    if (!playerId) {
      console.log('⚠️ Skipping player without ID:', player.name);
      continue;
    }

    const notificationData = {
      // Core fields
      userId: playerId,
      type: 'game_created',
      read: false,
      createdAt: new Date().toISOString(),
      clubId: game.clubId,

      // Game information
      gameId: game.id,
      gameName: game.name,
      gameDate: game.date,
      gameTime: game.time || '',
      gameVenue: game.venue || '',
      gameOpponent: game.opponent || '',

      // Team information
      teamId: teamId,
      teamName: teamName,

      // Notification content
      title: 'New Game - Availability Needed',
      message: `${game.name} scheduled for ${formatDate(game.date)}. Please provide your availability.`,

      // Player information (for tracking)
      recipientName: player.name || player.displayName || 'Player',
      recipientEmail: player.email || '',

      // Additional context
      askMealPreference: game.askMealPreference || false,
      tournamentId: game.tournamentId || null
    };

    notificationPromises.push(
      addDoc(notificationsRef, notificationData)
        .then(() => {
          successCount++;
          console.log(`✅ Notified ${player.name || playerId}`);
        })
        .catch(err => console.error(`❌ Failed to notify ${player.name}:`, err))
    );
  }

  await Promise.all(notificationPromises);

  console.log(`✅ Sent ${successCount} game notifications`);
  return {
    success: true,
    count: successCount,
    players: players.map(p => p.name || p.displayName || p.email)
  };
};

/**
 * Create notifications when an expense is split among players
 * Only notifies the affected players (playing 11/12)
 */
export const notifyPlayersAboutExpenseSplit = async (expense, game, affectedPlayerIds) => {
  try {
    console.log('💰 Creating expense notifications for affected players');

    if (!affectedPlayerIds || affectedPlayerIds.length === 0) {
      console.log('⚠️ No players affected by expense');
      return { success: true, count: 0 };
    }

    // Calculate per-player cost
    const totalAmount = expense.amount || 0;
    const playerCount = affectedPlayerIds.length;
    const perPlayerAmount = playerCount > 0 ? (totalAmount / playerCount).toFixed(2) : 0;

    // Handle super subs (50% discount)
    const superSubs = game?.superSubs || [];
    const regularPlayers = affectedPlayerIds.filter(id => !superSubs.includes(id));
    const superSubPlayers = affectedPlayerIds.filter(id => superSubs.includes(id));

    // Calculate adjusted amounts
    const regularShare = regularPlayers.length + (superSubPlayers.length * 0.5);
    const regularAmount = regularShare > 0 ? (totalAmount / regularShare).toFixed(2) : 0;
    const superSubAmount = (regularAmount * 0.5).toFixed(2);

    const notificationsRef = collection(db, 'notifications');
    const notificationPromises = [];

    for (const playerId of affectedPlayerIds) {
      const isSuperSub = superSubs.includes(playerId);
      const amountOwed = isSuperSub ? superSubAmount : regularAmount;

      // Get player details if possible
      let playerName = 'Player';
      try {
        const userRef = doc(db, 'users', playerId);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          playerName = userSnap.data().name || userSnap.data().displayName || 'Player';
        }
      } catch (err) {
        console.log('Could not fetch player name for:', playerId);
      }

      const notificationData = {
        // Core fields
        userId: playerId,
        type: 'expense_added',
        read: false,
        createdAt: new Date().toISOString(),
        clubId: expense.clubId,

        // Expense information
        expenseId: expense.id,
        expenseDescription: expense.description || 'Game Expense',
        expenseAmount: totalAmount,
        amountOwed: parseFloat(amountOwed),
        isSuperSub: isSuperSub,

        // Game information
        gameId: game?.id || expense.gameId,
        gameName: game?.name || expense.gameName || 'Game',
        gameDate: game?.date || expense.gameDate,

        // Notification content
        title: 'New Expense Added',
        message: `You owe $${amountOwed} for "${expense.description}" (${game?.name || 'Game'})`,

        // Additional context
        totalPlayers: affectedPlayerIds.length,
        recipientName: playerName
      };

      notificationPromises.push(
        addDoc(notificationsRef, notificationData)
          .then(() => console.log(`✅ Expense notification sent to ${playerName} - $${amountOwed}`))
          .catch(err => console.error(`❌ Failed to notify player:`, err))
      );
    }

    await Promise.all(notificationPromises);

    console.log(`✅ Sent ${notificationPromises.length} expense notifications`);
    return {
      success: true,
      count: notificationPromises.length,
      totalAmount: totalAmount,
      perPlayerAmount: { regular: regularAmount, superSub: superSubAmount }
    };
  } catch (error) {
    console.error('❌ Error creating expense notifications:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Mark notification as read and delete it
 * Called when user clicks on the notification
 */
export const markNotificationAsReadAndDelete = async (notificationId) => {
  try {
    console.log('🗑️ Marking notification as read and deleting:', notificationId);

    // Delete the notification document
    const notificationRef = doc(db, 'notifications', notificationId);
    await deleteDoc(notificationRef);

    console.log('✅ Notification deleted');
    return { success: true };
  } catch (error) {
    console.error('❌ Error deleting notification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all unread notifications for a user
 */
export const getUserNotifications = async (userId, clubId = null) => {
  try {
    const notificationsRef = collection(db, 'notifications');

    // Build query based on whether clubId is provided
    let q;
    if (clubId) {
      q = query(
        notificationsRef,
        where('userId', '==', userId),
        where('clubId', '==', clubId),
        where('read', '==', false)
      );
    } else {
      q = query(
        notificationsRef,
        where('userId', '==', userId),
        where('read', '==', false)
      );
    }

    const snapshot = await getDocs(q);
    const notifications = [];

    snapshot.forEach(doc => {
      notifications.push({
        id: doc.id,
        ...doc.data()
      });
    });

    // Sort by creation date, newest first
    notifications.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return { success: true, notifications };
  } catch (error) {
    console.error('❌ Error fetching notifications:', error);
    return { success: false, error: error.message, notifications: [] };
  }
};

/**
 * Delete all notifications for a specific game
 * Used when a game is deleted
 */
export const deleteGameNotifications = async (gameId, clubId) => {
  try {
    const notificationsRef = collection(db, 'notifications');
    const q = query(
      notificationsRef,
      where('gameId', '==', gameId),
      where('clubId', '==', clubId)
    );

    const snapshot = await getDocs(q);
    const deletePromises = [];

    snapshot.forEach(doc => {
      deletePromises.push(deleteDoc(doc.ref));
    });

    await Promise.all(deletePromises);

    console.log(`✅ Deleted ${deletePromises.length} notifications for game ${gameId}`);
    return { success: true, count: deletePromises.length };
  } catch (error) {
    console.error('❌ Error deleting game notifications:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete all notifications for a specific expense
 * Used when an expense is deleted
 */
export const deleteExpenseNotifications = async (expenseId, clubId) => {
  try {
    const notificationsRef = collection(db, 'notifications');
    const q = query(
      notificationsRef,
      where('expenseId', '==', expenseId),
      where('clubId', '==', clubId)
    );

    const snapshot = await getDocs(q);
    const deletePromises = [];

    snapshot.forEach(doc => {
      deletePromises.push(deleteDoc(doc.ref));
    });

    await Promise.all(deletePromises);

    console.log(`✅ Deleted ${deletePromises.length} notifications for expense ${expenseId}`);
    return { success: true, count: deletePromises.length };
  } catch (error) {
    console.error('❌ Error deleting expense notifications:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send payment reminder notifications
 * For players who owe money
 */
export const sendPaymentReminders = async (clubId, playerBalances) => {
  try {
    console.log('💳 Sending payment reminders');

    const notificationsRef = collection(db, 'notifications');
    const notificationPromises = [];

    for (const player of playerBalances) {
      if (player.balance <= 0) continue; // Skip if player doesn't owe money

      const notificationData = {
        userId: player.id,
        type: 'payment_reminder',
        read: false,
        createdAt: new Date().toISOString(),
        clubId: clubId,

        title: 'Payment Reminder',
        message: `You have an outstanding balance of $${player.balance.toFixed(2)}. Please make your payment.`,

        amountOwed: player.balance,
        recipientName: player.name,
        urgent: true
      };

      notificationPromises.push(
        addDoc(notificationsRef, notificationData)
          .then(() => console.log(`✅ Payment reminder sent to ${player.name}`))
          .catch(err => console.error(`❌ Failed to send reminder:`, err))
      );
    }

    await Promise.all(notificationPromises);

    console.log(`✅ Sent ${notificationPromises.length} payment reminders`);
    return {
      success: true,
      count: notificationPromises.length
    };
  } catch (error) {
    console.error('❌ Error sending payment reminders:', error);
    return { success: false, error: error.message };
  }
};

export default {
  notifyTeamPlayersAboutNewGame,
  processSquadPublishNotifications,
  notifyTeamRosterChange,
  notifyPlayersAboutExpenseSplit,
  markNotificationAsReadAndDelete,
  getUserNotifications,
  deleteGameNotifications,
  deleteExpenseNotifications,
  sendPaymentReminders,
  requestNotificationPermission,
  onForegroundMessage
};